import React from 'react';
import PropTypes from 'prop-types';
import './InlineNotice.css';

/**
 * InlineNotice — a tinted note inside the page flow (not a toast): a demo
 * mode hint, "the order can't be edited any more", "we mailed you a link".
 * Colour pair by `variant` (neutral = gray-075 / gray-9; the ui tones use
 * their background with gray-10 text and the tone on the icon + title —
 * `tone="text"` colours the running text in the tone too, the order
 * confirmation's green box). `textVariant` sets the text size: body-sm
 * (default) or body.
 */
const InlineNotice = ({
  variant = 'neutral',
  icon,
  title,
  children,
  action,
  bordered = false,
  size = 'm',
  textVariant = 'body-sm',
  tone = 'accent',
  role,
  className = '',
  ...rest
}) => (
  <div
    className={[
      'ds-inline-notice',
      `ds-inline-notice--${variant}`,
      `ds-inline-notice--${size}`,
      bordered && 'ds-inline-notice--bordered',
      tone === 'text' && 'ds-inline-notice--tone-text',
      className,
    ].filter(Boolean).join(' ')}
    role={role}
    {...rest}
  >
    {icon && <span className="ds-inline-notice__icon" aria-hidden="true">{icon}</span>}
    <div className="ds-inline-notice__body">
      {title && <p className="ds-inline-notice__title typo-h6">{title}</p>}
      {children && <div className={`ds-inline-notice__text typo-${textVariant}`}>{children}</div>}
      {action && <div className="ds-inline-notice__action">{action}</div>}
    </div>
  </div>
);

InlineNotice.propTypes = {
  variant: PropTypes.oneOf(['neutral', 'success', 'warning', 'error', 'info']),
  /** Leading icon (20px) */
  icon: PropTypes.node,
  title: PropTypes.node,
  children: PropTypes.node,
  /** A link or button under the text */
  action: PropTypes.node,
  /** 1px border in the tone colour */
  bordered: PropTypes.bool,
  /** 'm' medium-s padding · 's' small-l / medium-s */
  size: PropTypes.oneOf(['s', 'm']),
  /** Running text size: 'body-sm' (default) or 'body' */
  textVariant: PropTypes.oneOf(['body-sm', 'body']),
  /** 'accent' — tone on icon + title, gray-10 text · 'text' — the text in the tone colour too */
  tone: PropTypes.oneOf(['accent', 'text']),
  /** ARIA role — 'status' for a live note, 'alert' for an error */
  role: PropTypes.string,
  className: PropTypes.string,
};

export default InlineNotice;
